import type { EnterpriseSelection, RentalOrder } from '@super-mega/server/contracts/order'

/** Ordinary edits retain saved references; an explicit selection may replace parties before contracting. */
export function rentalEnterprisePayload(
  order: Pick<RentalOrder, 'subjectId' | 'companyInfoId' | 'identityInfoId' | 'canAssociateEnterprise'> | undefined,
  selection?: EnterpriseSelection
) {
  if (order && selection && !order.canAssociateEnterprise) {
    throw new Error('订单状态已变化，当前不能修改服务对象或资料，请刷新后核对')
  }
  if (!order || selection) {
    return { enterpriseSelection: selection ?? { mode: 'none' as const } }
  }
  return { subjectId: order.subjectId, companyInfoId: order.companyInfoId, identityInfoId: order.identityInfoId }
}

export function enterpriseSubmissionNotice(selections: Array<EnterpriseSelection | undefined>) {
  const newNames = selections.flatMap((selection) => (selection?.mode === 'new' ? [selection.draft.displayName] : []))
  const amendments = selections.filter((selection) => selection?.mode === 'existing' && selection.amendment).length
  return [
    newNames.length ? `将新建服务对象：${newNames.join('、')}，并保存已确认的企业、身份资料及关联。` : '',
    amendments ? `将更新 ${amendments} 个服务对象的待提交资料。` : ''
  ]
    .filter(Boolean)
    .join('')
}

/** Reuse on an unchanged retry, rotate after edits without persisting sensitive candidates. */
export function createOrderSubmissionKey() {
  let previous: string | undefined
  let key: string
  return (payload: unknown) => {
    const serialized = JSON.stringify(payload)
    if (serialized !== previous) {
      previous = serialized
      key = crypto.randomUUID()
    }
    return key
  }
}

/** Keep the exact failed edit, including its pre-submit references, even if a background refetch changes the order. */
export function createOrderEditSubmission<TPayload extends object>() {
  let pending: { fingerprint: string; payload: TPayload & { idempotencyKey: string } } | undefined
  return {
    async prepare(intent: unknown, buildPayload: () => TPayload | Promise<TPayload>) {
      const fingerprint = JSON.stringify(intent)
      if (pending?.fingerprint === fingerprint) {
        return pending.payload
      }
      const payload = { ...structuredClone(await buildPayload()), idempotencyKey: crypto.randomUUID() }
      pending = { fingerprint, payload }
      return payload
    },
    clear() {
      pending = undefined
    }
  }
}
